import React, { useMemo } from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Typography,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import DescriptionIcon from '@material-ui/icons/Description'
import GetAppIcon from '@material-ui/icons/GetApp'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'

interface DocumentPreviewModalProps {
    open: boolean
    onClose: () => void
    title: string
    fileUrl: string | null
    summary?: string | null
}

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp']

function fileExtension(fileUrl: string, title: string): string {
    const source = title.includes('.') ? title : fileUrl
    const clean = source.split('?')[0].toLowerCase()
    const dot = clean.lastIndexOf('.')
    return dot >= 0 ? clean.slice(dot) : ''
}

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
    open,
    onClose,
    title,
    fileUrl,
    summary,
}) => {
    const theme = useTheme()
    const extension = fileUrl ? fileExtension(fileUrl, title) : ''
    const isImage = IMAGE_EXTENSIONS.includes(extension)
    const isPdf = extension === '.pdf'

    const viewerUrl = useMemo(() => {
        if (!fileUrl || isImage) return ''
        if (isPdf) return fileUrl
        return `https://docs.google.com/gview?url=${encodeURIComponent(
            fileUrl,
        )}&embedded=true`
    }, [fileUrl, isImage, isPdf])

    const handleDownload = () => {
        if (!fileUrl) return
        const link = window.document.createElement('a')
        link.href = fileUrl
        link.download = title
        link.click()
    }

    const handleOpenInNewTab = () => {
        if (fileUrl) window.open(fileUrl, '_blank')
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                style: { height: '90vh', maxHeight: '90vh' },
            }}
        >
            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: theme.spacing(2),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: theme.spacing(2), minWidth: 0 }}
                >
                    <Box
                        style={{
                            height: 40,
                            width: 40,
                            borderRadius: theme.spacing(1),
                            backgroundColor: theme.palette.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.primary.contrastText,
                            flexShrink: 0,
                        }}
                    >
                        <DescriptionIcon />
                    </Box>
                    <Typography variant="h6" noWrap>
                        {title}
                    </Typography>
                </Box>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                style={{
                    padding: 0,
                    display: 'flex',
                    height: 'calc(90vh - 140px)',
                }}
            >
                {summary !== undefined && (
                    <Box
                        style={{
                            width: 320,
                            minWidth: 320,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            padding: theme.spacing(3),
                            overflowY: 'auto',
                        }}
                    >
                        <Typography
                            variant="overline"
                            style={{
                                color: theme.palette.text.secondary,
                                fontWeight: 600,
                                display: 'block',
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            AI Summary
                        </Typography>
                        <Box
                            style={{
                                backgroundColor: theme.palette.grey[100],
                                borderRadius: theme.spacing(1),
                                padding: theme.spacing(2),
                            }}
                        >
                            <Typography
                                variant="body2"
                                style={{
                                    lineHeight: 1.7,
                                    fontStyle: summary ? 'normal' : 'italic',
                                    color: summary
                                        ? theme.palette.text.primary
                                        : theme.palette.text.secondary,
                                }}
                            >
                                {summary ||
                                    'No summary available yet. The document may still be processing.'}
                            </Typography>
                        </Box>
                    </Box>
                )}
                <Box
                    style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.grey[100],
                    }}
                >
                    {!fileUrl ? (
                        <Typography color="textSecondary">
                            No document available
                        </Typography>
                    ) : isImage ? (
                        <img
                            src={fileUrl}
                            alt={title}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                        />
                    ) : (
                        <iframe
                            src={viewerUrl}
                            title={title}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                backgroundColor: theme.palette.background.paper,
                            }}
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions
                style={{
                    padding: theme.spacing(2),
                    borderTop: `1px solid ${theme.palette.divider}`,
                    justifyContent: 'space-between',
                }}
            >
                <Button
                    onClick={handleOpenInNewTab}
                    color="default"
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                    disabled={!fileUrl}
                >
                    Open in New Tab
                </Button>
                <Box display="flex" style={{ gap: theme.spacing(1) }}>
                    <Button
                        onClick={onClose}
                        color="default"
                        variant="outlined"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={handleDownload}
                        color="primary"
                        variant="contained"
                        startIcon={<GetAppIcon />}
                        disabled={!fileUrl}
                    >
                        Download
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}
